/* eslint-disable no-console */
import { groupBy, max } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { toast } from 'react-toastify';
import { formatGraphqlErrors } from './error';

/**
 * What the untyped callers pass to the number formatters: Intl converts null to 0, undefined to NaN and
 * numeric strings to their number, which `Number()` does the same way.
 */
type TFormattableNumber = number | string | null | undefined;

interface ILocalizeWizardOptions {
  formatter: (value: unknown) => unknown;
  keys?: string[];
}

interface IErrorPosition {
  line: number;
  column: number;
}

interface IFormatQueryByErrorPositionsArgs {
  queryString: string;
  errorPositions?: ReadonlyArray<IErrorPosition>;
}

/** An object or array whose entries can be read and replaced by key. */
const isTraversable = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const wait = (timeout = 3000) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, timeout);
  });

// export const isAndroid = () => {
//   const userAgent = navigator.userAgent.toLowerCase();
//   return userAgent.indexOf('android') > -1;
// };

export const isSmallScreen = () => {
  if (window.innerWidth <= 748) {
    return true;
  }
  return false;
};

export const spliceLabelOnMobile = <T>(value: T): T => {
  if (!isSmallScreen()) return value;
  if (typeof value !== 'string') return value;
  // if (value.length > 38) {
  //   return `${value.slice(0, 33)}...`;
  // }
  return value;
};

export const formatCurrency = (() => {
  const formatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  return (number: TFormattableNumber) => {
    const parts = formatter.formatToParts(Number(number));
    const strArray: string[] = [];
    // filter out the decimal part
    parts.forEach((part) => {
      if (['decimal', 'fraction'].includes(part.type)) return;
      strArray.push(part.value);
    });
    return strArray.join('');
  };
})();

export const formatValue = (value: TFormattableNumber) =>
  Intl.NumberFormat('de-DE').format(Number(value));

// The formatter replaces the values in place, so the result keeps the input's type only when it
// returns values of the same type (translation keys to translated strings).
export const localizeWizard = <T>(object: T, options: ILocalizeWizardOptions): T => {
  const {
    formatter,
    keys = [
      'title',
      'label',
      'subtitle',
      'description',
      'fieldLabel',
      'summaryTitle',
      'submitButtonLabel',
      'text',
      'note',
    ],
  } = options;
  const result = cloneDeep(object);
  function traverse(subtree: unknown) {
    if (!isTraversable(subtree)) return;
    Object.entries(subtree).forEach(([key, value]) => {
      if (keys.includes(key)) {
        if (subtree.skipI18n) return;
        // eslint-disable-next-line no-param-reassign
        subtree[key] = formatter(value);
      } else if (typeof value === 'object') {
        traverse(value);
      }
    });
  }
  traverse(result);
  return result;
};

export const changeLanguage = () => {
  let countClicks: string | number | null = window?.localStorage?.getItem('countClicksOnFooter');

  if (Number(countClicks) === 5) {
    countClicks = 0;

    const language = window?.localStorage?.getItem('language');
    window?.localStorage?.setItem('language', language === 'en' ? 'de' : 'en');

    document.location.reload();
  }

  if (!countClicks) window?.localStorage?.setItem('countClicksOnFooter', String(1));
  else window?.localStorage?.setItem('countClicksOnFooter', String(Number(countClicks) + 1));
};

// adds "^" symbol to query string where error exist
export const formatQueryByErrorPositions = ({
  queryString,
  errorPositions,
}: IFormatQueryByErrorPositionsArgs) => {
  if (!errorPositions) return queryString;
  try {
    const columnsByLine = groupBy(errorPositions, 'line');
    const lineNumbers = Object.keys(columnsByLine).sort((a, b) => Number(b) - Number(a));
    const queryStringLines = queryString.split('\n');
    lineNumbers.forEach((lineKey) => {
      const lineIndex = Number(lineKey);
      const columns = columnsByLine[lineKey].map((object) => object.column);
      const maxColumn = max(columns);
      queryStringLines.splice(lineIndex, 0, '');
      // `new Array(undefined)` holds one element, hence the length 1 without columns.
      const markers = new Array<string>(maxColumn ?? 1).fill(' ');
      columns.forEach((column) => {
        markers[column - 1] = '^';
      });
      queryStringLines[lineIndex] = markers.join('');
    });
    return queryStringLines.join('\n').trim();
  } catch (e) {
    console.error(e);
    return queryString;
  }
};

export { formatGraphqlErrors };

export const toastErrorMessage = (error: unknown) => {
  const e = formatGraphqlErrors(error);
  toast.error(e.message);
};

export const grabErrorMessage = (error: unknown) => formatGraphqlErrors(error).message;
